import React, { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import PanelDeleteIconButton from "../PanelDeleteIconButton";
import MutedText from "../../ui/MutedText";
import MenuSelect from "../../ui/MenuSelect";
import Tooltip from "../../ui/Tooltip";
import { useIsMobile } from "../../../hooks/useIsMobile";
import SensorAttrDragField from "./SensorAttrDragField";
import SensorChart from "./SensorChart";
import { getAttachmentLabel, getAttachmentState, getTargetLegendGlyph } from "./sensorTaxonomy";

const SensorRow = React.memo(
  ({
    sensor,
    sites,
    joints,
    actuators,
    bodies,
    tendons,
    worldBodies,
    bodyRootMap,
    onRemove,
    onUpdateAttr,
    onOpenAttachment,
  }) => {
    const isMobile = useIsMobile();
    const [expanded, setExpanded] = useState(false);
    const attachment = getAttachmentLabel(sensor);
    const attachmentState = useMemo(
      () =>
        getAttachmentState(sensor, {
          sites,
          joints,
          actuators,
          bodies,
          tendons,
          worldBodies,
          bodyRootMap,
        }),
      [sensor, sites, joints, actuators, bodies, tendons, worldBodies, bodyRootMap]
    );
    const attachmentLabel = attachmentState.attachType
      ? `${attachmentState.attachType[0].toUpperCase()}${attachmentState.attachType.slice(1)}`
      : null;

    const targetLegendGlyph = useMemo(
      () => getTargetLegendGlyph(attachmentState.attachType, sensor),
      [attachmentState.attachType, sensor]
    );
    const {
      IconComponent: TargetLegendIcon,
      iconCls: targetIconCls,
      colors: targetColors,
    } = targetLegendGlyph;
    const canOpenAttachment =
      !attachmentState.missing &&
      Boolean(attachmentState.value) &&
      ["joint", "actuator", "site"].includes(attachmentState.attachType);

    const attachmentPillClass = [
      "inline-flex w-fit max-w-[min(24rem,45vw)] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs leading-snug",
      "text-gray-200",
      canOpenAttachment
        ? "cursor-pointer border-transparent hover:border-gray-500/60 hover:bg-gray-700/35 focus:outline-none focus-visible:border-gray-400/70 focus-visible:ring-1 focus-visible:ring-gray-500/30"
        : "border-gray-400",
    ].join(" ");

    const handleOpenAttachment = (event) => {
      event.stopPropagation();
      if (!canOpenAttachment || typeof onOpenAttachment !== "function") return;
      onOpenAttachment({
        attachType: attachmentState.attachType,
        value: attachmentState.value,
      });
    };

    const attachmentPillContent = (
      <>
        <span
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: targetColors.base,
            border: `1.5px solid ${targetColors.stroke}`,
          }}
          aria-hidden
        >
          <TargetLegendIcon className={`${targetIconCls} text-white`} />
        </span>
        <span className="min-w-0 truncate">{attachmentState.displayLabel || attachment}</span>
      </>
    );

    return (
      <div
        className={[
          "group rounded border hover:border-gray-700",
          attachmentState.missing ? "border-amber-500/60 bg-amber-950/10" : "border-transparent",
        ].join(" ")}
      >
        <div
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3 shrink-0 panel-text-helper" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 panel-text-helper" />
          )}
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
            <div className="flex min-w-0 min-h-0 items-center gap-2">
              <span className="block min-h-0 min-w-0 max-w-[min(17rem,calc(100vw-120px))] truncate text-xs">
                <span className="font-medium text-gray-100">{sensor.name}</span>
                <span className="panel-text-helper">
                  {" · "}
                  {sensor.kind}
                </span>
              </span>
              {attachment ? (
                attachmentState.missing ? (
                  <Tooltip
                    content={`${attachmentLabel} target no longer exists: ${
                      attachmentState.displayLabel || attachment
                    }`}
                  >
                    <span
                      className={[
                        "inline-flex w-fit max-w-[min(24rem,45vw)] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs leading-snug",
                        "border-amber-500/70 bg-amber-950/30 text-amber-200",
                      ].join(" ")}
                    >
                      <AlertTriangle className="h-3 w-3 shrink-0 text-amber-300" aria-hidden />
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: targetColors.base,
                          border: `1.5px solid ${targetColors.stroke}`,
                        }}
                        aria-hidden
                      >
                        <TargetLegendIcon className={`${targetIconCls} text-white`} />
                      </span>
                      <span className="min-w-0 truncate">
                        {attachmentState.displayLabel || attachment}
                      </span>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    content={
                      canOpenAttachment
                        ? `Open ${attachmentState.attachType} properties`
                        : attachmentLabel
                          ? `${attachmentLabel}: ${attachmentState.displayLabel || attachment}`
                          : attachmentState.displayLabel || attachment
                    }
                  >
                    {canOpenAttachment ? (
                      <button
                        type="button"
                        className={attachmentPillClass}
                        onClick={handleOpenAttachment}
                        aria-label={`Open ${attachmentState.attachType} properties for ${attachment}`}
                      >
                        {attachmentPillContent}
                      </button>
                    ) : (
                      <span className={attachmentPillClass}>{attachmentPillContent}</span>
                    )}
                  </Tooltip>
                )
              ) : null}
            </div>
            <div className="min-w-0 flex-1" aria-hidden />
          </div>
          <span className="ml-auto shrink-0">
            <Tooltip content="Remove sensor">
              <PanelDeleteIconButton
                title=""
                ariaLabel="Remove sensor"
                revealOnParentHover={!isMobile}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(sensor.uuid);
                }}
                className="focus-visible:!pointer-events-auto focus-visible:!opacity-100"
                iconClassName="h-3.5 w-3.5"
              />
            </Tooltip>
          </span>
        </div>

        {expanded && (
          <div className="mx-2 mb-2 space-y-2 rounded border border-gray-700 bg-gray-800/50 px-3 py-2">
            <SensorChart sensorName={sensor.name} />
            {attachmentState.attachType && (
              <div className="space-y-1 pt-0.5">
                <div
                  className="flex min-w-0 w-full flex-row items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="inline-flex shrink-0 whitespace-nowrap text-xs text-gray-400">
                    {attachmentLabel}
                  </div>
                  <MenuSelect
                    value={attachmentState.value}
                    onChange={(value) =>
                      onUpdateAttr(sensor.uuid, { [attachmentState.attrKey]: value })
                    }
                    options={attachmentState.options}
                    placeholder={`Select ${attachmentState.attachType}...`}
                    disabled={attachmentState.options.length === 0}
                    size="compact"
                    className="min-w-0 flex-1"
                    triggerClassName={attachmentState.missing ? "border-amber-500/70" : ""}
                  />
                  {attachmentState.missing && (
                    <div className="flex shrink-0 items-center gap-1 text-[11px] text-amber-300">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      <span>Missing</span>
                    </div>
                  )}
                </div>
                {attachmentState.missing && (
                  <MutedText className="text-[11px] text-amber-200/90">
                    Choose an existing {attachmentState.attachType} to repair this sensor. It is not
                    deleted automatically so imported sensor intent is preserved.
                  </MutedText>
                )}
              </div>
            )}
            <div className="space-y-2 pt-0.5">
              <SensorAttrDragField
                label="Noise"
                attrKey="noise"
                sensor={sensor}
                onUpdateAttr={onUpdateAttr}
              />
              <SensorAttrDragField
                label="Cutoff"
                attrKey="cutoff"
                sensor={sensor}
                onUpdateAttr={onUpdateAttr}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default SensorRow;
